import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { startCase } from 'lodash';
import snakecaseKeys from 'snakecase-keys';

import { OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

import { clickAsLink } from 'helpers/link_helpers';
import MergeRecordsModal from 'components/modals/merge_records_modal';

import 'components/styles/merge_page.scss';

const personAttributes = [
  'firstName',
  'lastName',
  'middleInitial',
  'jobTitle',
  'linkedinUrl',
  'twitterUrl',
  'skypeName',
  'facebookUrl',
  'instagramUrl',
  'referralSource',
  'relationshipWithBrokerage',
  'contactSync',
  'companyName'
];

const readOnlyAttributes = [
  'address',
  'email',
  'phone',
  'secondaryCompanies'
];

export default class MergePeople extends React.Component {
  static propTypes = {
    mergeUrl: PropTypes.string.isRequired,
    people: PropTypes.array.isRequired,
    previousPageUrl: PropTypes.string.isRequired,
    userId: PropTypes.number.isRequired
  };

  requiredFields = [];

  constructor(props) {
    super(props);
    const keys = {};

    personAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        keys[key] = null;
      }
    });

    this.state = {
      showMergeModal: false,
      keys: keys
    };
  }

  findSelectedPerson = (id) => {
    return this.props.people.find(person => person.id === id);
  };

  getLabelFromKey = (key) => {
    if (key === 'companyName') return 'Primary Company';
    else return startCase(key);
  }

  handleCancelModal = () => {
    this.setState({ showMergeModal: false });
  };

  handleSubmit = () => {
    const keys = this.state.keys;
    personAttributes.map((key) => {
      if (!this.shouldDisplayRow(key) && key !== 'contactSync') {
        keys[key] = this.props.people[0].id;
      }
    });

    return axios.post(this.props.mergeUrl, {
      mappings: snakecaseKeys(keys),
      person_ids: this.props.people.map(person => person.id)
    }).finally(() => {
      window.location = this.props.previousPageUrl;
    });
  };

  onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const keys = {};

    personAttributes.map((key) => {
      keys[key] = id;
    });

    this.setState({ keys: keys });
  };

  onSelectKey = (id, key) => {
    this.setState(prevState => ({
      keys: {
        ...prevState.keys,
        [key]: parseInt(id)
      }
    }));
  };

  requiredKeysSelected = () => {
    const required = this.requiredFields.every((key) => {
      if (this.shouldDisplayRow(key)) {
        return this.state.keys[key] != null;
      } else {
        return true;
      }
    });

    if (this.shouldDisplaySyncRow()) {
      return required && this.state.keys["contactSync"] != null;
    } else {
      return required;
    }
  };

  selectedAll = (id) => {
    const selected = Object.keys(this.state.keys).every((key) => {
      return this.state.keys[key] === parseInt(id);
    });
    return selected;
  };

  shouldDisplaySyncRow() {
    const { people, userId } = { ...this.props };
    const nylasUsers = people.map((person) => {
      return person.nylasUsers.find(nu => nu.userId === userId);
    }).filter(Boolean);

    return nylasUsers.length > 1;
  }

  shouldDisplayRow(key) {
    const { people } = this.props;

    if (key === 'contactSync') return this.shouldDisplaySyncRow();

    const value =
      Array.isArray(this.props.people[0][key])
        ? this.props.people[0][key].toString()
        : this.props.people[0][key];

    let retVal = false;

    people.map((person, index) => {
      const val = Array.isArray(person[key]) ? person[key].toString() : person[key];
      if (index > 0 && (value || val)) {
        retVal = true;
      }
    });

    return retVal;
  }

  showMergeModal = () => {
    this.setState({ showMergeModal: true });
  };

  goBack = (e) => {
    clickAsLink(window, this.props.previousPageUrl, e);
  };

  render() {
    const disabled = !this.requiredKeysSelected();
    return (
      <div className="container">
        <div className="page-header">
          <div className="clickable preview-link" name="back-button" onClick={this.goBack}>
            &lt; Back
          </div>
          <h3>
            Merge Contacts
          </h3>
        </div>
        <div className="container merge-container white border-top-0">
          <p className="text-lg">
            In the table below, you will see all conflicting information
            between the contacts you have selected to merge. For each
            row, please select which data you would like to keep.
          </p>
          <p className="text-lg">
            Please note that all associations will be preserved.
          </p>
          <div>
            <Table className="merge-table">
              <thead>
                {this.renderHeaderColumns()}
              </thead>
              <tbody>
                {this.renderTableContent()}
              </tbody>
            </Table>
            <div className="merge-buttons">
              <button
                className="btn btn-sm btn-cancel"
                name="cancel-button"
                onClick={this.goBack}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-save left-buffer-md"
                disabled={disabled}
                name="merge-button"
                onClick={this.showMergeModal}
              >
                Merge
              </button>
            </div>
            {this.renderMergeModal()}
          </div>
        </div>
      </div>
    );
  }

  renderHeaderColumns() {
    const personHeaders = [];
    this.props.people.map((person, index) => {
      personHeaders.push(
        <th key={index}>
          <input
            checked={this.selectedAll(person.id)}
            className="checkbox--md"
            type="checkbox"
            value={person.id}
            onChange={this.onSelectAll}
          />
          <span className='left-buffer-md'> Contact {index + 1} </span>
        </th>
      );
    });

    return (
      <tr>
        <th> Fields </th>
        {personHeaders}
        <th> Merged Contact </th>
      </tr>
    );
  }

  renderContactSyncCheck(person) {
    const toolTip = (
      <Tooltip id={person.id}> This contact is currently syncing. Select this contact to update.</Tooltip>
    );
    return (
      <td key={person.id + "contactSync"}>
        <OverlayTrigger key={person.id} overlay={toolTip} placement="top">
          {this.renderInput(person, "contactSync")}
        </OverlayTrigger>
      </td>
    );
  }

  renderInput(person, key) {
    return (
      <input
        checked={this.state.keys[key] === person.id}
        className="checkbox--md"
        key={key}
        type="checkbox"
        value={person.id}
        onChange={() => this.onSelectKey(person.id, key)}
      />
    );
  }

  getValueFromKey(person, key) {
    if (key === 'address' && person[key]) {
      return person['addressFormatted'];
    } else if (key === 'phone') {
      return person['phoneFormatted'];
    } else if (key === 'secondaryCompanies') {
      return person[key].map(c => c.companyName).join("\n");
    } else {
      return Array.isArray(person[key]) ? person[key].join(', ') : person[key];
    }
  }

  renderTableContent() {
    const rows = personAttributes.map(key => this.renderRow(key)).filter(Boolean);
    rows.push(readOnlyAttributes.map(key => this.renderRow(key, true)).filter(Boolean));
    return rows;
  }

  renderRow(key, readOnly = false) {
    if (this.shouldDisplayRow(key)) {
      const personCells = [];
      const values = [];

      this.props.people.map((person) => {
        if (key === 'contactSync') {
          personCells.push(this.renderContactSyncCheck(person));
        } else {
          const value = this.getValueFromKey(person, key);
          values.push(value);
          personCells.push(
            <td key={person.id + key}>
              {!readOnly && this.renderInput(person, key)}
              <div className="d-inline-block left-buffer-sm">{value}</div>
            </td>
          );
        }
      });

      const id = this.state.keys[key];
      const person = this.findSelectedPerson(id);
      const newVal = id ? (this.getValueFromKey(person, key) || "") : "";

      const toolTip = (
        <Tooltip id={key}>All {this.getLabelFromKey(key)} fields will be merged into the new contact</Tooltip>
      );

      return (
        <tr key={key} name={key}>
          <td>
            {this.getLabelFromKey(key)}
            {readOnly && (
              <OverlayTrigger key={key} overlay={toolTip} placement="top">
                <FontAwesomeIcon className="ml-2 info-icon" icon={faInfoCircle}/>
              </OverlayTrigger>
            )}
          </td>
          {personCells}
          <td><div>{readOnly ? values.filter(Boolean).join('\n') : newVal}</div></td>
        </tr>
      );
    }
  }

  renderMergeModal() {
    return (
      <MergeRecordsModal
        recordType="Contacts"
        shown={this.state.showMergeModal}
        onCancel={this.handleCancelModal}
        onSubmit={this.handleSubmit}
      />
    );
  }
}
